import {
  Button,
  ButtonGroup,
  Form,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
  useDisclosure,
} from '@heroui/react'
import { useForm } from '@tanstack/react-form'
import { produce } from 'immer'
import { Plus, Trash } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import ConfirmDeleteModal from './ConfirmDeleteModal'
import EditRowButton from './EditRowButton'
import { tableClassNames, usePatchSchema, useSchema } from './utils'

function TypeSelect({ nested, label, placeholder, ...props }) {
  const options = [
    { label: 'String', value: 'string' },
    { label: 'Number', value: 'number' },
    { label: 'Boolean', value: 'boolean' },
  ]

  if (nested) {
    options.push({ label: 'Object', value: 'object' })
  }
  else {
    options.push({ label: 'Array', value: 'array' }, { label: 'Category', value: 'categorical' })
  }

  return (
    <Select
      variant="bordered"
      radius="sm"
      labelPlacement="outside"
      label={label}
      placeholder={placeholder}
      items={options}
      {...props}
    >
      {option => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>}
    </Select>
  )
}

function VariableModal({ variable, conditionIndex = undefined }) {
  const { isOpen, onOpen, onOpenChange } = useDisclosure()

  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')

  const { mutate: patchSchema } = usePatchSchema()
  const { data: selectedSchema } = useSchema(id)

  const isEdit = !!variable

  const form = useForm({
    defaultValues: variable
      ? {
          ...variable,
          // Convert string options to object format for editing
          options: Array.isArray(variable.options)
            ? variable.options.map(opt =>
                typeof opt === 'string' ? { value: opt, description: '' } : opt,
              )
            : [],
        }
      : {
          name: '',
          description: '',
          type: '',
          options: [],
        },
    onSubmit: async ({ value }) => {
      // Clean up options: convert back to strings if no descriptions
      if (value.type === 'categorical' && Array.isArray(value.options)) {
        const hasDescriptions = value.options.some(opt => opt.description && opt.description.trim() !== '')
        if (!hasDescriptions) {
          // Convert back to simple string array for backward compatibility
          value.options = value.options.map(opt =>
            typeof opt === 'object' ? opt.value : opt,
          )
        }
      }

      const updatedSchema = produce(selectedSchema, (draft) => {
        if (conditionIndex !== undefined) {
          draft.schema.conditions[conditionIndex].variables = isEdit
            ? draft.schema.conditions[conditionIndex].variables.map(v =>
                v.name === variable.name ? value : v,
              )
            : [...(draft.schema.conditions[conditionIndex].variables || []), value]
        }
        else {
          draft.schema.variables = isEdit
            ? draft.schema.variables.map(v =>
                v.name === variable.name ? value : v,
              )
            : [...(draft.schema.variables || []), value]
        }
      })

      await patchSchema(updatedSchema)
      if (patchSchema.error) {
        toast.error('Failed to update variable')
      }
      else {
        toast.success('Variable updated successfully')
      }
      onOpenChange()
    },
  })

  // Reset form on modal closure
  useEffect(() => {
    if (!isOpen) {
      form.reset()
    }
  }, [isOpen])

  return (
    <>
      {isEdit
        ? (
            <EditRowButton onPress={onOpen} />
          )
        : (
            <Button onPress={onOpen} variant="solid" color="primary" size="sm" radius="sm">
              <Plus size={16} />
              Add variable
            </Button>
          )}
      <Modal
        radius="sm"
        isOpen={isOpen}
        size="3xl"
        onOpenChange={onOpenChange}
        scrollBehavior="inside"
        classNames={{
          footer: 'ml-auto border-t border-gray-200 w-full',
          body: 'py-4',
          header: 'border-b border-gray-200 bg-[#18181b] w-full',
        }}
      >
        <ModalContent>
          {onClose => (
            <Form
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                form.handleSubmit()
              }}
              className="contents"
            >
              <ModalHeader className="flex flex-col gap-1">
                <h2 className={`
                  flex items-center gap-3 font-taurus text-2xl font-semibold
                  text-white
                `}
                >
                  {variable ? 'Edit variable' : 'Create new variable'}
                </h2>
                <p className={`
                  font-taurus text-xs font-light tracking-wider
                  text-foreground-400
                `}
                >
                  Create a new variable to extract information from your conversations.
                </p>
              </ModalHeader>
              <ModalBody>

                <form.Field
                  name="name"
                  children={(field) => {
                    return (
                      <Input
                        variant="bordered"
                        isRequired
                        radius="sm"
                        labelPlacement="outside"
                        placeholder="Enter a name for your new variable"
                        description="The name of the variable to extract from the call."
                        label="Name"
                        onValueChange={field.handleChange}
                        value={field.state.value}
                      />
                    )
                  }}
                />

                <form.Field
                  name="description"
                  children={(field) => {
                    return (
                      <Input
                        variant="bordered"
                        isRequired
                        radius="sm"
                        labelPlacement="outside"
                        placeholder="Enter a description for your new variable"
                        description="The LLM will use this description to extract the variable from the call."
                        label="Description"
                        onValueChange={field.handleChange}
                        value={field.state.value}
                      />
                    )
                  }}
                />

                <form.Field
                  name="type"
                  children={(field) => {
                    return (
                      <TypeSelect
                        label="Type"
                        isRequired
                        placeholder="Select a type"
                        description="This will determine how the variable is extracted from the call. Categorical variables are used to extract a single value from a list of options. Array variables are used to extract an array of similar values from the call."
                        onChange={e => field.handleChange(e.target.value)}
                        selectedKeys={[field.state.value]}
                      />
                    )
                  }}
                />

                <form.Subscribe
                  selector={state => state.values.type}
                >
                  {type => (
                    type === 'categorical'
                      ? (
                          <form.Field
                            name="options"
                            mode="array"
                          >
                            {field => (
                              <>
                                {field.state.value?.map((_, i) => (
                                  <div
                                    key={i}
                                    className={`
                                      flex w-full flex-col gap-2 rounded-md
                                      border border-gray-200 p-3
                                    `}
                                  >
                                    <div className={`
                                      flex w-full items-start gap-2
                                    `}
                                    >
                                      <form.Field name={`options[${i}].value`}>
                                        {optionField => (
                                          <Input
                                            variant="bordered"
                                            radius="sm"
                                            labelPlacement="outside"
                                            label="Option Value"
                                            placeholder="Enter option value"
                                            description="The value that will be extracted"
                                            onValueChange={optionField.handleChange}
                                            value={optionField.state.value || (typeof field.state.value[i] === 'string' ? field.state.value[i] : '')}
                                          />
                                        )}
                                      </form.Field>
                                      <Button
                                        variant="light"
                                        color="danger"
                                        radius="sm"
                                        isIconOnly
                                        size="sm"
                                        onPress={() => field.removeValue(i)}
                                        className="mt-6"
                                      >
                                        <Trash size={16} />
                                      </Button>
                                    </div>
                                    <form.Field name={`options[${i}].description`}>
                                      {descField => (
                                        <Input
                                          variant="bordered"
                                          radius="sm"
                                          labelPlacement="outside"
                                          label="Description (Optional)"
                                          placeholder="Describe when this option should be selected"
                                          description="Help the AI understand when to choose this option"
                                          onValueChange={descField.handleChange}
                                          value={descField.state.value || ''}
                                        />
                                      )}
                                    </form.Field>
                                  </div>
                                ))}
                                <Button
                                  onPress={() => field.pushValue({ value: '', description: '' })}
                                  radius="sm"
                                  size="sm"
                                  color="primary"
                                  variant="faded"
                                  className="shrink-0"
                                >
                                  <Plus size={16} />
                                  Add option
                                </Button>
                              </>
                            )}
                          </form.Field>
                        )
                      : type === 'array'
                        ? (
                            <div className="flex flex-col gap-2">
                              <form.Field name="items.type">
                                {subField => (
                                  <TypeSelect
                                    nested
                                    label="Item type"
                                    placeholder="Select an item type"
                                    description="The type of item to extract in the array."
                                    onChange={e => subField.handleChange(e.target.value)}
                                    selectedKeys={[subField.state.value]}
                                  />
                                )}
                              </form.Field>
                              <form.Subscribe
                                selector={state => state.values.items?.type}
                              >
                                {itemType => (
                                  itemType === 'object'
                                    ? (
                                        <>
                                          <form.Field
                                            name="items.minItems"
                                            mode="number"
                                          >
                                            {minItemsField => (
                                              <Input
                                                variant="bordered"
                                                radius="sm"
                                                type="number"
                                                labelPlacement="outside"
                                                placeholder="Enter a minimum number of items"
                                                description="The minimum number of items to extract in the array."
                                                onValueChange={minItemsField.handleChange}
                                                value={minItemsField.state.value}
                                              />
                                            )}
                                          </form.Field>
                                          <form.Field
                                            name="items.maxItems"
                                            mode="number"
                                          >
                                            {maxItemsField => (
                                              <Input
                                                variant="bordered"
                                                radius="sm"
                                                type="number"
                                                labelPlacement="outside"
                                                placeholder="Enter a maximum number of items"
                                                description="The maximum number of items to extract in the array."
                                                onValueChange={maxItemsField.handleChange}
                                                value={maxItemsField.state.value}
                                              />
                                            )}
                                          </form.Field>
                                          <form.Field
                                            name="items.properties"
                                            mode="array"
                                          >
                                            {propertyField => (
                                              <>
                                                {propertyField.state.value?.map((_, j) => (
                                                  <div
                                                    key={j}
                                                    className={`
                                                      flex w-full items-start
                                                      gap-2
                                                    `}
                                                  >
                                                    <form.Field name={`items.properties[${j}].name`}>
                                                      {subPropertyField => (
                                                        <Input
                                                          variant="bordered"
                                                          radius="sm"
                                                          labelPlacement="outside"
                                                          placeholder="Enter a name"
                                                          description="The name of the property to include in the object."
                                                          onValueChange={subPropertyField.handleChange}
                                                          value={subPropertyField.state.value}
                                                        />
                                                      )}
                                                    </form.Field>
                                                    <form.Field name={`items.properties[${j}].description`}>
                                                      {subPropertyField => (
                                                        <Input
                                                          variant="bordered"
                                                          radius="sm"
                                                          labelPlacement="outside"
                                                          placeholder="Enter a description"
                                                          description="The LLM will use this description to extract the property from the call."
                                                          onValueChange={subPropertyField.handleChange}
                                                          value={subPropertyField.state.value}
                                                        />
                                                      )}
                                                    </form.Field>
                                                    <form.Field name={`items.properties[${j}].type`}>
                                                      {subPropertyField => (
                                                        <TypeSelect
                                                          nested
                                                          placeholder="Select a property type"
                                                          onChange={e => subPropertyField.handleChange(e.target.value)}
                                                          selectedKeys={[subPropertyField.state.value]}
                                                        />
                                                      )}
                                                    </form.Field>
                                                    <Button
                                                      variant="light"
                                                      color="danger"
                                                      radius="sm"
                                                      isIconOnly
                                                      size="sm"
                                                      onPress={() => propertyField.removeValue(j)}
                                                    >
                                                      <Trash size={16} />
                                                    </Button>
                                                  </div>
                                                ))}
                                                <Button
                                                  onPress={() => propertyField.pushValue({ type: 'string' })}
                                                  radius="sm"
                                                  size="sm"
                                                  variant="light"
                                                  className={`
                                                    mt-2 mr-auto text-sm
                                                    text-primary
                                                  `}
                                                >
                                                  <Plus size={16} />
                                                  Add property
                                                </Button>
                                              </>
                                            )}
                                          </form.Field>
                                        </>
                                      )
                                    : null
                                )}
                              </form.Subscribe>
                            </div>
                          )
                        : (type === 'string' || type === 'number') && (
                            <form.Field name="regex">
                              {field => (
                                <Input
                                  variant="bordered"
                                  radius="sm"
                                  labelPlacement="outside"
                                  placeholder="Enter a regex pattern (optional)"
                                  description="The regex pattern to use to extract the variable from the call."
                                  label="Regex Pattern"
                                  onValueChange={field.handleChange}
                                  value={field.state.value}
                                />
                              )}
                            </form.Field>
                          )
                  )}
                </form.Subscribe>

              </ModalBody>
              <ModalFooter>
                <Button color="danger" radius="sm" variant="light" onPress={onClose}>
                  Close
                </Button>
                <Button color="primary" radius="sm" type="submit">
                  {isEdit ? 'Update' : 'Create'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}

export default function VariableTable({ conditionIndex = undefined }) {
  const [searchParams] = useSearchParams()
  const id = searchParams.get('schema')

  const { data: selectedSchema } = useSchema(id)
  const { mutate: patchSchema } = usePatchSchema()

  const [isDeleting, setIsDeleting] = useState(false)
  const [variableToDelete, setVariableToDelete] = useState(null)

  const variables = useMemo(() => (conditionIndex !== undefined)
    ? selectedSchema.schema.conditions[conditionIndex].variables
    : selectedSchema.schema.variables, [selectedSchema, conditionIndex])

  async function handleDeleteVariable(variableName) {
    const updatedSchema = produce(selectedSchema, (draft) => {
      if (conditionIndex !== undefined) {
        // Remove from condition variables
        draft.schema.conditions[conditionIndex].variables = draft.schema.conditions[conditionIndex].variables.filter(v => v.name !== variableName)
      }
      else {
        // Remove from main variables
        draft.schema.variables = draft.schema.variables.filter(v => v.name !== variableName)

        // Also remove from any groupings that reference this variable
        if (draft.schema.groupings) {
          draft.schema.groupings.forEach((grouping) => {
            if (grouping.variables) {
              grouping.variables = grouping.variables.filter(varName => varName !== variableName)
            }
          })
        }
      }
    })

    await patchSchema(updatedSchema)
    if (patchSchema.error) {
      toast.error('Failed to delete variable')
    }
    else {
      toast.success('Variable deleted successfully')
    }
    setIsDeleting(false)
    setVariableToDelete(null)
  }

  return (
    <div className="relative w-full">
      <Table
        classNames={tableClassNames}
        radius="none"
        aria-label="Variables"
      >
        <TableHeader>
          <TableColumn>Name</TableColumn>
          <TableColumn>Type</TableColumn>
          <TableColumn>Description</TableColumn>
          <TableColumn align="end" />
        </TableHeader>
        <TableBody emptyContent={(
          <div className="mt-24 flex h-full w-full flex-col items-center">
            <h1 className="mb-6 text-3xl font-semibold tracking-tighter">
              No variables yet.
            </h1>
          </div>
        )}
        >
          {variables?.map(variable => (
            <TableRow key={variable.name}>
              <TableCell className="font-medium">{variable.name}</TableCell>
              <TableCell className={`
                font-mono text-xs tracking-tighter text-gray-500
              `}
              >
                {variable.type}
              </TableCell>
              <TableCell className="text-gray-500">{variable.description}</TableCell>
              <TableCell>
                <ButtonGroup className="gap-2">
                  <VariableModal conditionIndex={conditionIndex} key={variable.name} variable={variable} />
                  <ConfirmDeleteModal
                    isOpen={isDeleting && variableToDelete === variable.name}
                    onOpenChange={() => {
                      setIsDeleting(false)
                      setVariableToDelete(null)
                    }}
                    onConfirm={() => handleDeleteVariable(variable.name)}
                    title="Delete Variable"
                    message="Are you sure you want to delete this variable?"
                  >
                    <Button
                      isIconOnly
                      onPress={() => {
                        setVariableToDelete(variable.name)
                        setIsDeleting(true)
                      }}
                      variant="faded"
                      color="danger"
                      size="sm"
                      disableRipple
                    >
                      <Trash size={16} />
                    </Button>
                  </ConfirmDeleteModal>
                </ButtonGroup>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="absolute -right-3.5 -bottom-12">
        <VariableModal conditionIndex={conditionIndex} />
      </div>
    </div>
  )
}
